import React, { useState } from 'react';
import { PartyPopper, Star, Gift, Bell } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { showSuccessToast, showInfoToast, showErrorToast } from '../utils/toastConfig';

const EventosPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) {
      showErrorToast(toast, 'Por favor completa tu nombre y email.');
      return;
    }
    setLoading(true);
    try {
      await api.post('users/subscribers/', { email, name });
      showSuccessToast(toast, '¡Genial! Te avisaremos de todos nuestros eventos especiales.');
      setEmail('');
      setName('');
    } catch (err: any) {
      if (err.response?.status === 400) {
        showInfoToast(toast, 'Ya estás en nuestra lista VIP');
      } else {
        showErrorToast(toast, 'No pudimos procesar tu solicitud. Inténtalo más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen pt-40 pb-20 px-4" style={{ backgroundColor: '#FFFBF7' }}>
      <div className="max-w-4xl mx-auto">
        {/* Icono y badge */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#B4897F]/10 rounded-full mb-6 animate-bounce">
            <PartyPopper className="w-10 h-10 text-[#B4897F]" />
          </div>
          <div className="inline-block bg-gradient-to-r from-[#B4897F] to-[#A67A6F] text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
            ¡Vienen Sorpresas!
          </div>
        </div>

        {/* Título principal */}
        <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 text-center mb-6">
          Eventos Especiales
        </h1>
        
        <p className="text-xl text-gray-600 text-center mb-12 max-w-2xl mx-auto">
          Estamos organizando eventos únicos y experiencias inolvidables. ¡No te los pierdas!
        </p>

        {/* Tarjetas de tipos de eventos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-sm border-2 border-gray-100 hover:border-[#B4897F] transition-all duration-300 hover:shadow-lg">
            <div className="w-14 h-14 bg-gradient-to-br from-[#B4897F]/20 to-[#B4897F]/10 rounded-full flex items-center justify-center mb-4">
              <Star className="w-7 h-7 text-[#B4897F]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Eventos VIP</h3>
            <p className="text-gray-600">
              Experiencias exclusivas para nuestros clientes más especiales con tratamientos premium
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-sm border-2 border-gray-100 hover:border-[#B4897F] transition-all duration-300 hover:shadow-lg">
            <div className="w-14 h-14 bg-gradient-to-br from-[#B4897F]/20 to-[#B4897F]/10 rounded-full flex items-center justify-center mb-4">
              <Gift className="w-7 h-7 text-[#B4897F]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Lanzamientos</h3>
            <p className="text-gray-600">
              Sé el primero en conocer nuestros nuevos productos y servicios en eventos especiales
            </p>
          </div>
        </div>

        {/* Sección de notificaciones */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-[#B4897F]/20 overflow-hidden">
          <div className="bg-gradient-to-r from-[#B4897F] to-[#A67A6F] p-8 text-white text-center">
            <Bell className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-3xl font-serif font-bold mb-3">
              ¡Mantente al Tanto!
            </h2>
            <p className="text-white/90 max-w-xl mx-auto">
              Regístrate para recibir invitaciones exclusivas a nuestros eventos y promociones especiales
            </p>
          </div>
          
          <div className="p-8">
            <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Tu nombre"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B4897F] focus:border-transparent"
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                  title="Por favor ingresa un email válido (ej: nombre@ejemplo.com)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B4897F] focus:border-transparent"
                />
              </div>
              <button 
                type="submit"
                disabled={loading}
                className="w-full bg-[#B4897F] text-white py-3 font-semibold hover:bg-[#A67A6F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Enviando...' : 'Quiero Estar en la Lista'}
              </button>
              <p className="text-xs text-gray-500 text-center">
                No spam, solo eventos especiales y promociones exclusivas
              </p>
            </form>
          </div>
        </div>

        {/* Mensaje adicional */}
        <div className="text-center mt-12">
          <p className="text-gray-500">
            Síguenos en redes sociales para no perderte ninguna novedad
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <a 
              href="https://instagram.com/frahda.studio" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 bg-[#B4897F]/10 rounded-full flex items-center justify-center hover:bg-[#B4897F]/20 transition-colors"
            >
              <span className="text-[#B4897F] text-xl">📸</span>
            </a>
            <a 
              href="https://facebook.com/frahda.studio" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 bg-[#B4897F]/10 rounded-full flex items-center justify-center hover:bg-[#B4897F]/20 transition-colors"
            >
              <span className="text-[#B4897F] text-xl">👍</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventosPage;
